import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  usePayersNameListQuery,
  useCheckPatientPrimaryInsuranceStatusMutation,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting';
import { MedicareFormShape } from '../../hooks/useMedicareForm';

interface UseMedicareInsuranceEntryProps {
  patient_id: string;
}

export const useMedicareInsuranceEntry = ({
  patient_id,
}: UseMedicareInsuranceEntryProps) => {
  const [payersQuery, setPayersQuery] = useState('');
  const [verifiedMBI, setVerifiedMBI] = useState<string | null>(null);

  const { control, getValues, watch } = useFormContext<MedicareFormShape>();

  const { data = { payers: [] } } = usePayersNameListQuery({
    search: payersQuery,
    type: 'medicare',
  });

  const { mutateAsync: checkInsuranceStatus, isPending: isCheckStatusPending } =
    useCheckPatientPrimaryInsuranceStatusMutation({ patient_id });

  const currentMBI = watch('entry.mbi');
  const verified = currentMBI === verifiedMBI && !!verifiedMBI;

  const handleCheckInsuranceStatus = async () => {
    const values = getValues('entry');
    try {
      const { data: result } = await checkInsuranceStatus({
        payer_name: values?.payer_name?.name || '',
        payer_id: values?.payer_name?.id || '',
        payer_type: 'medicare',
        mbi: values?.mbi ?? '',
        payment_type: values?.payment_type as never,
      });
      if (result) {
        setVerifiedMBI(result.verified ? (values?.mbi ?? null) : null);
      }
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    control,
    payers: data.payers,
    payersQuery,
    setPayersQuery,
    verified,
    isCheckStatusPending,
    handleCheckInsuranceStatus,
  };
};
